<template>
    <auth-layout>
        <div class="w-full md:w-1/2 flex flex-col">
            <div
                class="flex flex-col justify-center md:justify-start my-auto pt-8 md:pt-0 px-8 md:px-24 lg:px-32"
                v-if="state == 'processing'"
            >
                <span class="text-center text-5xl md:text-2xl lg:text-4xl xl:text-6xl font-bold" data-cy="form-header">
                    <template v-if="isEpicvaultEnabled">
                        <img
                            class="inline-block h-12 md:h-6 lg:h-8 xl:h-12"
                            :src="'/images/icon-epic-vault.png'"
                            alt="logo"
                        />
                        <span class="text-black align-bottom">Epic</span
                        ><span class="text-default-blue align-bottom">Vault</span>
                    </template>
                    <template v-else>
                        <img class="inline-block h-12 md:h-6 lg:h-8 xl:h-12" :src="'/images/icon.png'" alt="logo" />
                        <span class="text-black align-bottom">Claims</span
                        ><span class="text-default-purple align-bottom">Vault</span>
                    </template>
                </span>

                <div v-if="passwordExpiryState" class="reset-body mt-8 -mb-8 font-normal">
                    {{ $t('auth.change_password.body') }}
                </div>

                <form-reset
                    :reset="reset"
                    :validation="validation"
                    :processing="processing"
                    :token="getToken"
                    :user_id="user_id"
                    :theme="getTheme"
                    @formSubmit="handleSubmit"
                    data-cy="form_reset_password"
                >
                </form-reset>

                <div v-if="!passwordExpiryState" class="text-center pt-12 pb-12">
                    <p>
                        <router-link :to="{ name: 'login' }" class="underline" data-cy="back_to_login">
                            {{ $t('auth.links.login') }}
                        </router-link>
                    </p>
                </div>
                <div v-if="passwordExpiryState" class="text-center pt-12 pb-12">
                    <div @click="handleLogout" class="text-center cursor-pointer" data-cy="back_to_login">
                        {{ $t('auth.links.logout') }}
                    </div>
                </div>
            </div>

            <div
                class="flex flex-col justify-center md:justify-start my-auto pt-8 md:pt-0 px-8 md:px-24 lg:px-32"
                v-if="state == 'success'"
                data-cy="reset_password_success"
            >
                <span class="text-center text-5xl md:text-2xl lg:text-4xl xl:text-6xl font-bold" data-cy="form-header">
                    <template v-if="isEpicvaultEnabled">
                        <img
                            class="inline-block h-12 md:h-6 lg:h-8 xl:h-12"
                            :src="'/images/icon-epic-vault.png'"
                            alt="logo"
                        />
                        <span class="text-black align-bottom">Epic</span
                        ><span class="text-default-blue align-bottom">Vault</span>
                    </template>
                    <template v-else>
                        <img class="inline-block h-12 md:h-6 lg:h-8 xl:h-12" :src="'/images/icon.png'" alt="logo" />
                        <span class="text-black align-bottom">Claims</span
                        ><span class="text-default-purple align-bottom">Vault</span>
                    </template>
                </span>

                <div class="reset-header">{{ $t('auth.reset.token_success.header') }}</div>
                <div v-if="!passwordExpiryState" class="reset-body">{{ $t('auth.reset.token_success.body') }}</div>
                <div v-if="passwordExpiryState" class="reset-body">{{ $t('auth.reset.change_success.body') }}</div>

                <div v-if="!passwordExpiryState" class="py-3 px-6 mb-6 text-center">
                    <router-link to="/login">
                        <div class="text-center">
                            {{ $t('auth.links.login') }}
                        </div>
                    </router-link>
                </div>

                <div v-if="passwordExpiryState" class="py-3 px-6 mb-6 text-center">
                    <router-link to="/">
                        <div class="text-center">
                            {{ $t('auth.links.home') }}
                        </div>
                    </router-link>
                </div>
            </div>

            <div
                class="flex flex-col justify-center md:justify-start my-auto pt-8 md:pt-0 px-8 md:px-24 lg:px-32"
                v-if="state == 'error'"
                data-cy="reset_password_error"
            >
                <span class="text-center text-3xl font-bold" data-cy="form-header">
                    <template v-if="isEpicvaultEnabled">
                        <img
                            class="inline-block h-12 md:h-6 lg:h-8 xl:h-12"
                            :src="'/images/icon-epic-vault.png'"
                            alt="logo"
                        />
                        <span class="text-black align-bottom">Epic</span
                        ><span class="text-default-blue align-bottom">Vault</span>
                    </template>
                    <template v-else>
                        <img class="inline-block h-12 md:h-6 lg:h-8 xl:h-12" :src="'/images/icon.png'" alt="logo" />
                        <span class="text-black align-bottom">Claims</span
                        ><span class="text-default-purple align-bottom">Vault</span>
                    </template>
                </span>

                <div class="reset-header">{{ $t('auth.reset.token_invalid.header') }}</div>
                <div class="reset-body">{{ $t('auth.reset.token_invalid.body') }}</div>
            </div>

            <div
                class="flex flex-col justify-center md:justify-start my-auto pt-8 md:pt-0 px-8 md:px-24 lg:px-32"
                v-if="state == 'expired'"
                data-cy="reset_password_expired"
            >
                <span class="text-center text-5xl md:text-2xl lg:text-4xl xl:text-6xl font-bold" data-cy="form-header">
                    <template v-if="isEpicvaultEnabled">
                        <img
                            class="inline-block h-12 md:h-6 lg:h-8 xl:h-12"
                            :src="'/images/icon-epic-vault.png'"
                            alt="logo"
                        />
                        <span class="text-black align-bottom">Epic</span
                        ><span class="text-default-blue align-bottom">Vault</span>
                    </template>
                    <template v-else>
                        <img class="inline-block h-12 md:h-6 lg:h-8 xl:h-12" :src="'/images/icon.png'" alt="logo" />
                        <span class="text-black align-bottom">Claims</span
                        ><span class="text-default-purple align-bottom">Vault</span>
                    </template>
                </span>

                <div class="reset-header">{{ $t('auth.reset.token_expired.header') }}</div>
                <div class="reset-body">{{ $t('auth.reset.token_expired.body') }}</div>
            </div>
        </div>
    </auth-layout>
</template>

<script>
import System from '@/models/system.js';
import AuthLayout from '@/layouts/AuthLayout.vue';
import FormReset from '@/components/forms/auth/Reset.vue';

export default {
    components: {
        AuthLayout,
        FormReset,
    },
    data() {
        return {
            state: '',
            user_id: this.$store.state.user.id,
            reset: {
                password: '',
                confirm_password: '',
            },
            validation: {
                errors: {},
                message: '',
            },
            processing: false,
        };
    },
    computed: {
        isEpicvaultEnabled() {
            return this.$store.state.system.is_epicvault_enabled;
        },
        getTheme() {
            return this.isEpicvaultEnabled ? 'default' : 'claimsvault';
        },
        getToken() {
            return this.$route.params.token;
        },
        passwordExpiryState() {
            return this.$store.state.user.is_change_password ? true : false;
        },
    },
    mounted() {
        this.$http
            .post('/api/password/is-valid/' + this.$route.params.token)
            .then((response) => {
                if (response.data.status == 'valid') {
                    this.state = 'processing';
                } else if (this.passwordExpiryState) {
                    this.state = 'processing';
                } else if (response.data.status == 'expired') {
                    this.state = 'expired';
                } else {
                    this.state = 'error';
                }
            })
            .catch((error) => {
                this.$notification.error({
                    message: this.$t('globals.global.notifications.error.any', { code: 'CVPIS01' }),
                });
            });
    },
    methods: {
        handleLogout() {
            this.$http.get('/api/logout').then((response) => {
                System.resetData();
                this.$router.go('/login');
            });
        },
        handleSubmit: function () {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            if (this.passwordExpiryState) {
                let payload = {
                    password: this.reset.password,
                    password_confirmation: this.reset.confirm_password,
                };

                this.processing = true;
                this.$http
                    .post('/api/users/self/change-password-expired', payload)
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.state = 'success';
                            this.$notification.success({ message: response.data.message });
                        } else {
                            this.$notification.error({ message: response.data.message });
                        }
                    })
                    .catch((error) => {
                        this.$notification.error({ message: error });

                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCP01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                let payload = {
                    token: this.$route.params.token,
                    password: this.reset.password,
                    password_confirmation: this.reset.confirm_password,
                };

                this.$http
                    .post('/api/password/reset', payload)
                    .then((response) => {
                        if (response.data.status == 'error') {
                            this.$notification.error({ message: response.data.message });
                        } else {
                            this.state = 'success';
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVPR01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
    },
};
</script>
